import React, {useState} from 'react'
import { Link } from 'react-router-dom'
import yaml from 'js-yaml'
import {
  BooleanInput,
  FormWithRedirect,
  TextInput,
  SaveButton,
  useMutation,
  useNotify,
  useQuery,
  useRedirect,
  required
} from 'react-admin'
import { makeStyles, Grid, Paper, Toolbar } from '@material-ui/core'
import { AceEditorInput, Button, SectionTitle } from '../../components'
import { useAuth } from '../../utils/auth'

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(3)
  },
  form: {
    padding: theme.spacing(2)
  },
  toolbar: {
    backgroundColor: 'inherit',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  button: {
    marginLeft: theme.spacing(1)
  }
}))

const _getRedirectPath = pathname => {
  let idx = pathname.indexOf('/scheduled_tasks')
  return `${pathname.substring(0, idx)}/show?tab=3`
}

const _canEdit = (pathname, auth) => {
  if (pathname.endsWith('/show')) {
    return false
  }
  return auth.canEditDeployments()
}

const DeploymentTaskEdit = ({ location, match }) => {
  const classes = useStyles()
  const deploymentId = match.params.id
  const taskId = match.params.task_id
  const redirectPath = _getRedirectPath(location.pathname)
  const notify = useNotify()
  const redirect = useRedirect()
  const auth = useAuth()
  const [invalidConfig, setInvalidConfig] = useState(false)
  const canEdit = _canEdit(location.pathname, auth)

  const { data: task } = useQuery(
    {
      type: 'getOne',
      resource: `deployments/${deploymentId}/scheduled_tasks`,
      payload: { id: taskId }
    },
    {
      onFailure: () => {
        notify('ra.notification.item_doesnt_exist', 'warning')
        redirect(redirectPath)
      }
    }
  )

  const [save, { loading }] = useMutation()
  const onSave = ({ id, name, config, enabled }) => {
    const data = {
      name,
      config: yaml.safeLoad(config),
      enabled,
      schemaVersion: task.schemaVersion,
      sermosPipelineId: task.sermosPipelineId
    }
    save(
      {
        type: 'update',
        resource: `deployments/${deploymentId}/scheduled_tasks`,
        payload: { id, data }
      },
      {
        onSuccess: () => {
          notify('Elemented updated', 'info')
          redirect(redirectPath)
        },
        onFailure: error => {
          if (error.status !== 422) {
            notify(
              typeof error === 'string'
                ? error
                : error.message || 'ra.notification.http_error',
              'warning'
            )
          } else {
            console.log(error.body)
            if (error.body.config !== undefined) {
              setInvalidConfig(true)
            }
          }
        }
      }
    )
  }

  if (!task) {
    return null
  }

  let record = {
    id: task.id,
    name: task.name,
    config: yaml.dump(task.config),
    enabled: task.enabled,
    sermosScheduledTaskId: task.sermosScheduledTaskId
  }

  return (
    <div className={classes.root}>
      <SectionTitle
        title={canEdit ? 'Edit Scheduled Task' : 'Scheduled Task'}
      />
      <Paper className={classes.content}>
        <FormWithRedirect
          record={record}
          save={onSave}
          saving={loading}
          redirect={redirectPath}
          render={formProps => (
            <form>
              <Grid container className={classes.form} spacing={2}>
                <Grid item xs={12} lg={6}>
                  <TextInput
                    source="id"
                    label="ID"
                    variant="standard"
                    disabled
                    fullWidth
                  />
                  <TextInput
                    source="sermosScheduledTaskId"
                    label="Sermos Scheduled Task ID"
                    variant="standard"
                    disabled
                    fullWidth
                  />
                  <TextInput
                    source="name"
                    label="Name"
                    variant="standard"
                    validate={[required()]}
                    disabled={!canEdit}
                    fullWidth
                  />
                  <AceEditorInput
                    source="config"
                    label="Task Config"
                    invalidValue={invalidConfig}
                    validate={[required()]}
                  />
                  <BooleanInput source="enabled" label="Enabled" defaultValue={false} />
                </Grid>
              </Grid>
              <Toolbar className={classes.toolbar}>
                <span style={{ flex: 1 }} />
                {canEdit && (
                  <SaveButton
                    size="small"
                    saving={formProps.saving}
                    handleSubmitWithRedirect={
                      formProps.handleSubmitWithRedirect
                    }
                  />
                )}
                <Button
                  className={classes.button}
                  component={Link}
                  to={redirectPath}
                  label="Cancel"
                  variant="contained"
                  color="default"
                />
              </Toolbar>
            </form>
          )}
        />
      </Paper>
    </div>
  )
}

export default DeploymentTaskEdit
